import { Button } from "@/components/ui/button";
import {
  CHAT_THREAD_DIALOG_CLASS_NAME,
  CHAT_DIALOG_DESCRIPTION_CLASS_NAME,
} from "@/features/chat/chat-dialog-styles";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { queryClient } from "@/ajax/queryClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import { ChatThreadBody } from "./chat-thread-body";

type OrganizationSupportChatDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  threadId: string | null;
  initialMessage: string;
};

export function OrganizationSupportChatDialog({
  open,
  onOpenChange,
  threadId,
  initialMessage,
}: OrganizationSupportChatDialogProps) {
  const trpc = useTRPC();
  const messagesQuery = useQuery(
    trpc.chat.listChatThreadMessages.queryOptions(
      { threadId: threadId ?? "" },
      {
        enabled: open && Boolean(threadId),
        staleTime: 0,
        refetchInterval: () => 5_000 + Math.random() * 5_000,
      },
    ),
  );
  const sendMessageMutation = useMutation(
    trpc.chat.sendMessage.mutationOptions({
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries(
          trpc.chat.listChatThreadMessages.queryOptions({
            threadId: variables.threadId,
          }),
        );
      },
      onError: () => {
        toast.error("Unable to send message. Please try again.");
      },
    }),
  );

  const handleSendMessage = async (
    message: string,
    attachmentIds?: string[],
  ) => {
    if (!threadId) {
      throw new Error("Support chat thread is not ready");
    }

    await sendMessageMutation.mutateAsync({
      threadId,
      message,
      attachmentIds,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={CHAT_THREAD_DIALOG_CLASS_NAME}>
        <DialogHeader className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 pr-14 text-left sm:px-5 sm:pr-14">
          <DialogTitle>Saile Support</DialogTitle>
          <DialogDescription className={CHAT_DIALOG_DESCRIPTION_CLASS_NAME}>
            Chat with Saile about upgrading your organization&apos;s account.
          </DialogDescription>
        </DialogHeader>
        {!threadId || messagesQuery.isLoading ? (
          <div className="flex flex-1 items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : messagesQuery.error ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <p className="text-sm text-destructive">
              Unable to load the conversation.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => messagesQuery.refetch()}
            >
              Try again
            </Button>
          </div>
        ) : (
          <ChatThreadBody
            messages={messagesQuery.data ?? []}
            onSendMessage={handleSendMessage}
            threadId={threadId}
            viewMode="org"
            initialMessage={initialMessage}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
